import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Chip,
  Snackbar,
  Alert,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Divider,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
  ListItemText,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import userService from '../../services/userServices';
import roleService from '../../services/roleService'; // Import du service roleService
import { useAuth } from '../../context/AuthContext';
import { buildRoleMapping, getIconByName, getRoleIcon, getRoleColor, getRoleLabel } from '../../utils/roleUtils';

// Define base roles constant
const BASE_ROLES = ['admin', 'owner', 'tenant'];

/**
 * Composant pour la gestion des utilisateurs
 */
const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    isAdmin: false,
    isOwner: false,
    isTenant: false,
    verificationStatus: 'pending'
  });
  const [formErrors, setFormErrors] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [availableRoles, setAvailableRoles] = useState([]); // Rôles disponibles depuis l'API
  const [selectedSupplementaryRoles, setSelectedSupplementaryRoles] = useState([]); // Renamed state for clarity
  const [userRoles, setUserRoles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [roleMapping, setRoleMapping] = useState({});
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  // Charger les données au montage du composant
  useEffect(() => {
    fetchUsers();
    fetchAvailableRoles(); // Charger les rôles disponibles
  }, []);

  /**
   * Récupérer tous les utilisateurs
   */
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);

      // Récupérer les rôles pour chaque utilisateur
      const rolesPromises = data.map(user => fetchUserRolesData(user.id));
      const rolesResults = await Promise.allSettled(rolesPromises);

      const userRolesMap = {};
      rolesResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          userRolesMap[data[index].id] = result.value;
        } else {
          console.error(`Failed to fetch roles for user ${data[index].id}:`, result.reason);
          userRolesMap[data[index].id] = []; // Set empty array on failure
        }
      });

      console.log('User roles map:', userRolesMap);
      setUserRoles(userRolesMap);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification('Erreur lors du chargement des utilisateurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Récupérer les rôles disponibles depuis le service
   */
  const fetchAvailableRoles = useCallback(async () => {
    try {
      console.log('##Start Fetching available roles...');
      const roles = await roleService.getAllRoles();
      setAvailableRoles(roles);
      console.log('Available roles fetched:', roles);

      // Construire le roleMapping à partir des rôles disponibles
      const mapping = buildRoleMapping(roles);
      setRoleMapping(mapping);
      console.log('*** FIN Role mapping built:', mapping);
    } catch (error) {
      console.error('Error fetching available roles:', error);
      showNotification('Erreur lors du chargement des rôles disponibles', 'error');
      setAvailableRoles([]);
    }
  }, []);

  /**
   * Récupérer les rôles d'un utilisateur spécifique
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Array>} - Liste des détails des rôles de l'utilisateur
   */
  const fetchUserRolesData = async (userId) => {
    try {
      // Utiliser getUserRoleDetails qui retourne directement les détails des rôles
      const roleDetails = await userService.getUserRoleDetails(userId);
      console.log(`Role details fetched for user ${userId}:`, roleDetails);
      return roleDetails || []; // Ensure it returns an array
    } catch (error) {
      console.error(`Error fetching role details for user ${userId}:`, error);
      return []; // Return empty array on error
    }
  };

  /**
   * Gérer le tri des colonnes
   */
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Filtrer et trier les utilisateurs
   */
  const getSortedAndFilteredUsers = () => {
    let filtered = users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      return (
          user.name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.phone?.toLowerCase().includes(searchLower)
      );
    });

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Gérer les valeurs null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Gérer les dates
      if (orderBy === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Comparaison
      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  };

  const filteredUsers = getSortedAndFilteredUsers();

  /**
   * Formatter une date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Créer un composant TableCell avec tri
   */
  const SortableTableCell = ({ label, property, children }) => (
    <TableCell sortDirection={orderBy === property ? order : false}>
      <TableSortLabel
          active={orderBy === property}
          direction={orderBy === property ? order : 'asc'}
          onClick={() => handleSort(property)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  /**
   * Ouvrir le dialogue de création d'utilisateur
   */
  const handleOpenCreateDialog = () => {
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      isAdmin: false,
      isOwner: false,
      isTenant: false,
      verificationStatus: 'pending'
    });
    setSelectedSupplementaryRoles([]); // Reset supplementary roles
    setFormErrors({});
    setOpenDialog(true);
  };

  /**
   * Ouvrir le dialogue de modification d'utilisateur
   * @param {Object} user - Utilisateur à modifier
   */
  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);

    const userRolesList = userRoles[user.id] || [];
    console.log(`Opening edit for user ${user.id}, roles:`, userRolesList);

    // Separate base roles (for switches) and supplementary roles (for Select)
    let isAdmin = false;
    let isOwner = false;
    let isTenant = false;
    const supplementaryRoles = [];

    userRolesList.forEach(role => {
      if (role.name === 'admin') {
        isAdmin = true;
      } else if (role.name === 'owner') {
        isOwner = true;
      } else if (role.name === 'tenant') {
        isTenant = true;
      } else {
        // Only add non-base roles to the supplementary list
        supplementaryRoles.push(role.name);
      }
    });

    console.log('Separated roles:', { isAdmin, isOwner, isTenant, supplementaryRoles });

    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      isAdmin: isAdmin, // Set from separated roles
      isOwner: isOwner,   // Set from separated roles
      isTenant: isTenant, // Set from separated roles
      verificationStatus: user.verificationStatus || 'pending'
    });

    setSelectedSupplementaryRoles(supplementaryRoles); // Set only supplementary roles

    setFormErrors({});
    setOpenDialog(true);
  };


  /**
   * Ouvrir le dialogue de suppression d'utilisateur
   * @param {Object} user - Utilisateur à supprimer
   */
  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  /**
   * Ouvrir le dialogue de visualisation d'utilisateur
   * @param {Object} user - Utilisateur à visualiser
   */
  const handleOpenViewDialog = (user) => {
    setSelectedUser(user);
    setOpenViewDialog(true);
  };

  /**
   * Fermer tous les dialogues et réinitialiser le formulaire
   */
  const handleCloseDialogs = () => {
    setOpenDialog(false);
    setOpenDeleteDialog(false);
    setOpenViewDialog(false);
    setSelectedUser(null);
    // Reset form data
    setFormData({
      name: '',
      email: '',
      phone: '',
      isAdmin: false,
      isOwner: false,
      isTenant: false,
      verificationStatus: 'pending'
    });
    setSelectedSupplementaryRoles([]); // Reset supplementary roles
    setFormErrors({});
    setSubmitting(false); // Ensure submitting state is reset
  };

  /**
   * Gérer les changements dans le formulaire (TextFields, Switches, Select status)
   */
  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;

    // Log the change
    console.log(`Input change: name=${name}, type=${type}, value=${value}, checked=${checked}`);

    setFormData(prev => {
      const newState = {
        ...prev,
        [name]: type === 'checkbox' || type === 'switch' ? checked : value
      };
      console.log('New formData state:', newState);
      return newState;
    });

    // Clear validation error for the field being changed
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };


  /**
   * Gérer les changements de rôles supplémentaires sélectionnés (Select component)
   */
  const handleSupplementaryRoleChange = (event) => {
    const {
      target: { value },
    } = event;
    // value is the array of selected role names
    const newSelectedRoles = typeof value === 'string' ? value.split(',') : value;
    console.log('Supplementary roles changed:', newSelectedRoles);
    setSelectedSupplementaryRoles(newSelectedRoles);
  };


  /**
   * Valider le formulaire
   */
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Le nom est requis';
    }
    if (!formData.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'L\'email n\'est pas valide';
    }
    // Add other validations if needed
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Soumettre le formulaire (Création ou Mise à jour)
   */
  const handleSubmit = async () => {
    if (!validateForm()) {
      console.warn('Form validation failed:', formErrors);
      return;
    }
    setSubmitting(true);
    console.log('Submitting form with formData:', formData);
    console.log('Selected supplementary roles:', selectedSupplementaryRoles);

    // --- Consolidate roles ---
    // Start with supplementary roles and add base roles based on switches
    const finalRolesSet = new Set(selectedSupplementaryRoles);
    if (formData.isAdmin) {
      finalRolesSet.add('admin');
    }
    if (formData.isOwner) {
      finalRolesSet.add('owner');
    }
    if (formData.isTenant) {
      finalRolesSet.add('tenant');
    }
    const finalRolesArray = Array.from(finalRolesSet);
    console.log('Final consolidated roles to send:', finalRolesArray);
    // --- End Consolidate roles ---

    try {
      // Prepare user data, excluding the individual boolean flags
      // The backend expects a single 'roles' array
      const { isAdmin, isOwner, isTenant, ...restFormData } = formData;
      const userData = {
        ...restFormData,
        roles: finalRolesArray // Send the consolidated array
      };

      console.log('Data being sent to backend:', userData);

      if (selectedUser) {
        console.log(`Updating user ID: ${selectedUser.id}`);
        await userService.updateUser(selectedUser.id, userData);
        showNotification('Utilisateur mis à jour avec succès', 'success');
      } else {
        console.log('Creating new user');
        await userService.createUser(userData);
        showNotification('Utilisateur créé avec succès', 'success');
      }
      handleCloseDialogs();
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de l\'enregistrement de l\'utilisateur';
      showNotification(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };


  /**
   * Supprimer un utilisateur
   */
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setSubmitting(true); // Disable buttons during delete
    try {
      console.log(`Deleting user ID: ${selectedUser.id}`);
      await userService.deleteUser(selectedUser.id);
      showNotification('Utilisateur supprimé avec succès', 'success');
      handleCloseDialogs();
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Erreur lors de la suppression de l\'utilisateur', 'error');
      setSubmitting(false); // Re-enable buttons on error
    }
    // No finally block needed here as handleCloseDialogs resets submitting state
  };

  /**
   * Afficher une notification
   */
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  /**
   * Fermer la notification
   */
  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Filter available roles to exclude base roles for the Select component
  const supplementaryRolesOptions = availableRoles.filter(role => !BASE_ROLES.includes(role.name));

  return (
      <Box sx={{ p: 3 }}>
        {/* Header: Title, Search, Add Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Gestion des Utilisateurs
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
                placeholder="Rechercher..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
            />
            <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
            >
              Nouvel utilisateur
            </Button>
          </Box>
        </Box>

        {/* User Table */}
        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
        ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableTableCell label="Nom" property="name" />
                    <SortableTableCell label="Email" property="email" />
                    <SortableTableCell label="Téléphone" property="phone" />
                    <SortableTableCell label="Date de création" property="createdAt" />
                    <SortableTableCell label="Rôles" property="roles" />
                    <SortableTableCell label="Statut" property="verificationStatus" />
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone || '-'}</TableCell>
                        <TableCell>{formatDate(user.createdAt)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {/* Display roles fetched for the user */}
                            {userRoles[user.id]?.map(role => (
                                <Tooltip
                                    key={role.id || role.name} // Use name as fallback key
                                    title={role.description || role.name}
                                    arrow
                                >
                                  <Chip
                                      icon={getRoleIcon(role, roleMapping)}
                                      label={getRoleLabel(role, roleMapping)}
                                      size="small"
                                      color={
                                        role.name === 'admin' ? 'error' :
                                            role.name === 'owner' ? 'primary' :
                                                role.name === 'tenant' ? 'success' :
                                                    'default'
                                      }
                                      variant={
                                        BASE_ROLES.includes(role.name) ?
                                            'filled' : 'outlined'
                                      }
                                      style={{
                                        borderColor: role.color || getRoleColor(role, roleMapping),
                                        color: BASE_ROLES.includes(role.name) ?
                                            undefined : (getRoleColor(role, roleMapping) || role.color)
                                      }}
                                  />
                                </Tooltip>
                            ))}
                            {(!userRoles[user.id] || userRoles[user.id].length === 0) && (
                                <Typography variant="caption" color="text.secondary">
                                  Aucun rôle
                                </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                              label={user.verificationStatus || 'verified'} // Default to verified if null/undefined
                              color={
                                user.verificationStatus === 'verified' ? 'success' :
                                    user.verificationStatus === 'pending' ? 'warning' : 'error'
                              }
                              size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Tooltip title="Voir les détails">
                              <IconButton
                                  color="info"
                                  size="small"
                                  onClick={() => handleOpenViewDialog(user)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Modifier">
                              <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleOpenEditDialog(user)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              {/* Disable delete for the current logged-in user */}
                              <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleOpenDeleteDialog(user)}
                                  disabled={user.id === currentUser?.id}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                  ))}
                  {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Aucun utilisateur trouvé
                        </TableCell>
                      </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        )}

        {/* Create/Edit Dialog */}
        <Dialog
            open={openDialog}
            onClose={handleCloseDialogs}
            maxWidth="md"
            fullWidth
        >
          <DialogTitle>
            {selectedUser ? `Modifier l'utilisateur: ${selectedUser.name}` : 'Créer un nouvel utilisateur'}
          </DialogTitle>
          <DialogContent>
            <Box component="form" noValidate sx={{ mt: 2 }}>
              <Grid container spacing={3}> {/* Increased spacing */}
                {/* Basic Info */}
                <Grid item xs={12}>
                  <TextField
                      name="name"
                      label="Nom complet"
                      fullWidth
                      value={formData.name}
                      onChange={handleInputChange}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment>,
                      }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                      name="email"
                      label="Email"
                      type="email"
                      fullWidth
                      value={formData.email}
                      onChange={handleInputChange}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment>,
                      }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                      name="phone"
                      label="Téléphone"
                      type="tel"
                      fullWidth
                      value={formData.phone}
                      onChange={handleInputChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment>,
                      }}
                  />
                </Grid>

                {/* Base Roles */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}><Typography variant="overline">Rôles principaux</Typography></Divider>
                </Grid>
                <Grid item xs={12} container justifyContent="space-around">
                  <FormControlLabel
                      control={
                        <Switch
                            checked={formData.isAdmin}
                            onChange={handleInputChange}
                            name="isAdmin"
                            color="error"
                        />
                      }
                      label="Administrateur"
                      sx={{ flexGrow: 1, justifyContent: 'center' }}
                  />
                  <FormControlLabel
                      control={
                        <Switch
                            checked={formData.isOwner}
                            onChange={handleInputChange}
                            name="isOwner"
                            color="primary"
                        />
                      }
                      label="Propriétaire"
                      sx={{ flexGrow: 1, justifyContent: 'center' }}
                  />
                  <FormControlLabel
                      control={
                        <Switch
                            checked={formData.isTenant}
                            onChange={handleInputChange}
                            name="isTenant"
                            color="success"
                        />
                      }
                      label="Locataire"
                      sx={{ flexGrow: 1, justifyContent: 'center' }}
                  />
                </Grid>

                {/* Supplementary Roles */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}><Typography variant="overline">Rôles supplémentaires</Typography></Divider>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="supplementary-roles-select-label">Rôles supplémentaires</InputLabel>
                    <Select
                        labelId="supplementary-roles-select-label"
                        multiple
                        value={selectedSupplementaryRoles}
                        onChange={handleSupplementaryRoleChange}
                        label="Rôles supplémentaires" // Add label for accessibility
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {/* Render chips for selected supplementary roles */}
                              {selected.map((roleName) => (
                                  <Chip
                                      key={roleName}
                                      label={getRoleLabel(roleName, roleMapping)}
                                      size="small"
                                      icon={getRoleIcon(roleName, roleMapping)}
                                      style={{
                                        borderColor: getRoleColor(roleName, roleMapping),
                                        color: getRoleColor(roleName, roleMapping)
                                      }}
                                      variant="outlined" // Always outlined for supplementary
                                  />
                              ))}
                            </Box>
                        )}
                    >
                      {/* Only show non-base roles as options */}
                      {supplementaryRolesOptions.length === 0 && (
                          <MenuItem disabled>Aucun rôle supplémentaire disponible</MenuItem>
                      )}
                      {supplementaryRolesOptions.map((role) => (
                          <MenuItem key={role.id} value={role.name}>
                            <Checkbox checked={selectedSupplementaryRoles.includes(role.name)} />
                            <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {getRoleIcon(role, roleMapping)}
                                    <Box component="span" sx={{ ml: 1, color: role.color || getRoleColor(role, roleMapping) }}>
                                      {role.description || role.name}
                                    </Box>
                                  </Box>
                                }
                                secondary={role.description ? role.name : undefined}
                            />
                          </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Sélectionnez un ou plusieurs rôles (hors admin, propriétaire, locataire)</FormHelperText>
                  </FormControl>
                </Grid>

                {/* Status */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}><Typography variant="overline">Statut</Typography></Divider>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="verification-status-label">Statut de vérification</InputLabel>
                    <Select
                        labelId="verification-status-label"
                        name="verificationStatus"
                        value={formData.verificationStatus}
                        onChange={handleInputChange}
                        label="Statut de vérification"
                    >
                      <MenuItem value="pending">En attente</MenuItem>
                      <MenuItem value="verified">Vérifié</MenuItem>
                      <MenuItem value="rejected">Rejeté</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}> {/* Added padding */}
            <Button onClick={handleCloseDialogs} color="inherit" disabled={submitting}>
              Annuler
            </Button>
            <Button
                onClick={handleSubmit}
                color="primary"
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {submitting ? 'Enregistrement...' : (selectedUser ? 'Mettre à jour' : 'Créer')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
            open={openDeleteDialog}
            onClose={handleCloseDialogs}
        >
          <DialogTitle>
            Confirmer la suppression
          </DialogTitle>
          <DialogContent>
            <Typography>
              Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{selectedUser?.name}</strong> ?
              Cette action est irréversible.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs} color="inherit" disabled={submitting}>
              Annuler
            </Button>
            <Button
                onClick={handleDeleteUser}
                color="error"
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {submitting ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View User Details Dialog */}
        <Dialog
            open={openViewDialog}
            onClose={handleCloseDialogs}
            maxWidth="md"
            fullWidth
        >
          <DialogTitle>
            Détails de l'utilisateur: {selectedUser?.name}
          </DialogTitle>
          <DialogContent>
            {selectedUser && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {/* Basic Info Panel */}
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="h6">
                            Informations générales
                          </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {selectedUser.email}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {selectedUser.phone || 'Non renseigné'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                    {/* Roles Panel */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                          Rôles
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {userRoles[selectedUser.id]?.map(role => (
                              <Tooltip
                                  key={role.id || role.name}
                                  title={role.description || role.name}
                                  arrow
                              >
                                <Chip
                                    icon={getRoleIcon(role, roleMapping)}
                                    label={getRoleLabel(role, roleMapping)}
                                    size="small" // Consistent size
                                    color={
                                      role.name === 'admin' ? 'error' :
                                          role.name === 'owner' ? 'primary' :
                                              role.name === 'tenant' ? 'success' :
                                                  'default'
                                    }
                                    variant={
                                      BASE_ROLES.includes(role.name) ?
                                          'filled' : 'outlined'
                                    }
                                    style={{
                                      borderColor: role.color || getRoleColor(role, roleMapping),
                                      color: BASE_ROLES.includes(role.name) ?
                                          undefined : (role.color || getRoleColor(role, roleMapping)),
                                    }}
                                />
                              </Tooltip>
                          ))}
                          {(!userRoles[selectedUser.id] || userRoles[selectedUser.id].length === 0) && (
                              <Typography color="text.secondary" variant="body2">
                                Aucun rôle attribué
                              </Typography>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                    {/* Status Panel */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                          Statut & Dates
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box>
                          <Chip
                              label={selectedUser.verificationStatus || 'verified'}
                              color={
                                selectedUser.verificationStatus === 'verified' ? 'success' :
                                    selectedUser.verificationStatus === 'pending' ? 'warning' : 'error'
                              }
                              size="small" // Consistent size
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Inscrit le: {new Date(selectedUser.createdAt).toLocaleDateString()}
                          </Typography>
                          {selectedUser.updatedAt && (
                              <Typography variant="body2" color="text.secondary">
                                Dernière MàJ: {new Date(selectedUser.updatedAt).toLocaleDateString()}
                              </Typography>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialogs} color="inherit">
              Fermer
            </Button>
            <Button
                onClick={() => {
                  handleCloseDialogs();
                  // Ensure selectedUser is passed correctly
                  if (selectedUser) {
                    handleOpenEditDialog(selectedUser);
                  }
                }}
                color="primary"
                variant="outlined" // Use outlined for secondary action
            >
              Modifier
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbar */}
        <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
              onClose={handleCloseNotification}
              severity={notification.severity}
              variant="filled" // Use filled alerts for better visibility
              sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default UserManagement;